import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The National Policy Committee's closed forum.
 *
 * The gate is the server's: every endpoint below sits behind the
 * `policy.committee` middleware, which re-derives the committee from the
 * offices held today on each request. `useForumAccessQuery` is the one call
 * that is allowed to come back "no" — the portal uses it to decide whether
 * to show the module, and nothing more. Hiding the menu entry is a courtesy,
 * not the security.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type ForumPerson = {
  id: number;
  name: string;
  pro_pic?: string | null;
  /** The office they sit here by — "Past President", "Board of Trustees". */
  seat?: string | null;
};

export type ForumAttachment = {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  /** An API endpoint, not a storage link: it re-checks membership. */
  url: string;
};

export type ForumThreadSummary = {
  id: number;
  title: string;
  excerpt: string;
  author: ForumPerson | null;
  is_pinned: boolean;
  is_locked: boolean;
  replies_count: number;
  last_activity_at: string | null;
  created_at: string;
  is_unread: boolean;
  can_edit: boolean;
};

export type ForumPost = {
  id: number;
  body: string;
  author: ForumPerson | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete: boolean;
  attachments: ForumAttachment[];
};

export type ForumThreadDetail = {
  id: number;
  title: string;
  body: string;
  author: ForumPerson | null;
  is_pinned: boolean;
  is_locked: boolean;
  replies_count: number;
  created_at: string;
  updated_at: string;
  /** Where the reader had got to before this visit — draws the "new" line. */
  last_read_at: string | null;
  can_edit: boolean;
  can_moderate: boolean;
  attachments: ForumAttachment[];
  posts: ForumPost[];
};

export type ForumAccess = {
  allowed: boolean;
  can_moderate: boolean;
  seat: string | null;
};

export type ForumMemberGroup = {
  group: string;
  label: string;
  people: { name: string; seat: string }[];
};

type Paginated<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Send FormData when files are attached, plain JSON otherwise.
 *
 * Same shape as the support module's, and for the same reason: multipart on
 * every request would turn every reply into a file upload as far as the
 * 5-minute timeout in apiSlice is concerned.
 */
function buildBody(fields: Record<string, unknown>, files?: File[]) {
  if (!files?.length) return fields;

  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v == null) return;
    // PHP reads (bool)"false" as true, so booleans go over as "1"/"0".
    if (typeof v === "boolean") fd.append(k, v ? "1" : "0");
    else fd.append(k, String(v));
  });
  files.forEach((f) => fd.append("attachments[]", f));
  return fd;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const forumApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** Am I on the committee? The only endpoint outside the gate. */
    forumAccess: builder.query<ForumAccess, void>({
      query: () => "/forum/me",
      providesTags: ["ForumAccess"],
    }),

    forumMembers: builder.query<{ total: number; groups: ForumMemberGroup[] }, void>({
      query: () => "/forum/members",
      providesTags: ["ForumMembers"],
    }),

    forumThreads: builder.query<Paginated<ForumThreadSummary>, { q?: string; page?: number } | void>({
      query: (args) => ({
        url: "/forum/threads",
        params: { q: args?.q || undefined, page: args?.page || 1 },
      }),
      providesTags: ["ForumThreads"],
    }),

    forumThread: builder.query<ForumThreadDetail, number>({
      query: (id) => `/forum/threads/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ForumThread" as const, id }],
    }),

    createForumThread: builder.mutation<ForumThreadDetail, { title: string; body: string; files?: File[] }>({
      query: ({ title, body, files }) => ({
        url: "/forum/threads",
        method: "POST",
        body: buildBody({ title, body }, files),
      }),
      invalidatesTags: ["ForumThreads"],
    }),

    // POST, not PATCH: the host in front of the API rejects PATCH outright.
    updateForumThread: builder.mutation<
      ForumThreadDetail,
      { id: number; title?: string; body?: string; files?: File[] }
    >({
      query: ({ id, title, body, files }) => ({
        url: `/forum/threads/${id}`,
        method: "POST",
        body: buildBody({ title, body }, files),
      }),
      invalidatesTags: (_r, _e, { id }) => ["ForumThreads", { type: "ForumThread" as const, id }],
    }),

    deleteForumThread: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/forum/threads/${id}`, method: "DELETE" }),
      invalidatesTags: ["ForumThreads"],
    }),

    pinForumThread: builder.mutation<ForumThreadDetail, { id: number; pinned: boolean }>({
      query: ({ id, pinned }) => ({
        url: `/forum/threads/${id}/pin`,
        method: "POST",
        body: { pinned: pinned ? 1 : 0 },
      }),
      invalidatesTags: (_r, _e, { id }) => ["ForumThreads", { type: "ForumThread" as const, id }],
    }),

    lockForumThread: builder.mutation<ForumThreadDetail, { id: number; locked: boolean }>({
      query: ({ id, locked }) => ({
        url: `/forum/threads/${id}/lock`,
        method: "POST",
        body: { locked: locked ? 1 : 0 },
      }),
      invalidatesTags: (_r, _e, { id }) => ["ForumThreads", { type: "ForumThread" as const, id }],
    }),

    replyToForumThread: builder.mutation<ForumPost, { threadId: number; body: string; files?: File[] }>({
      query: ({ threadId, body, files }) => ({
        url: `/forum/threads/${threadId}/posts`,
        method: "POST",
        body: buildBody({ body }, files),
      }),
      invalidatesTags: (_r, _e, { threadId }) => [
        "ForumThreads",
        { type: "ForumThread" as const, id: threadId },
      ],
    }),

    updateForumPost: builder.mutation<ForumPost, { id: number; threadId: number; body: string; files?: File[] }>({
      query: ({ id, body, files }) => ({
        url: `/forum/posts/${id}`,
        method: "POST",
        body: buildBody({ body }, files),
      }),
      invalidatesTags: (_r, _e, { threadId }) => [{ type: "ForumThread" as const, id: threadId }],
    }),

    deleteForumPost: builder.mutation<{ message: string }, { id: number; threadId: number }>({
      query: ({ id }) => ({ url: `/forum/posts/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { threadId }) => [
        "ForumThreads",
        { type: "ForumThread" as const, id: threadId },
      ],
    }),
  }),
});

export const {
  useForumAccessQuery,
  useForumMembersQuery,
  useForumThreadsQuery,
  useForumThreadQuery,
  useCreateForumThreadMutation,
  useUpdateForumThreadMutation,
  useDeleteForumThreadMutation,
  usePinForumThreadMutation,
  useLockForumThreadMutation,
  useReplyToForumThreadMutation,
  useUpdateForumPostMutation,
  useDeleteForumPostMutation,
} = forumApi;

/**
 * Fetch a private attachment and hand it to the browser as a download.
 *
 * It cannot be an <a href> or an <img src>: the file is served by an
 * authenticated API route, and neither of those sends the bearer token. So
 * the bytes are fetched with the token, turned into a blob URL and released
 * straight after.
 */
export async function downloadForumAttachment(url: string, filename: string) {
  const token = localStorage.getItem("token");

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  if (!res.ok) {
    throw new Error(
      res.status === 403
        ? "This file is open to the Policy Committee only."
        : "That file could not be opened.",
    );
  }

  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
